import { instanceToInstance, Type } from "class-transformer";
import { Observable } from "rxjs";

import { User } from "../auth/models/user.model";
import { Negotiation } from "../company/modules/commercial/models/negotiation.model";
import { Contract } from "../company/modules/contracts/models/contract.model";
import { Address } from './address.model';
import { Assignee } from "./assignee.model";
import { Company, companyCan } from "./company.model";
import { Location } from './location.model';
import { Product } from "./product.model";
import { Slot } from "./slot.model";
import { parseApiBoolean } from "../utilities/json-tools";

export class SlotHistory {
  @Type(() => Date)
  readonly date: Date;

  @Type(() => Company)
  readonly company: Company;

  readonly action: 'ACCEPTED_BATCH_PARTIALLY' | 'ACCEPTED_BATCH' | 'ASSIGNED_SLOT' | 'CREATE_BATCH' | 'REJECTED_BATCH' | 'RETURNED_BATCH' | 'SENT_BATCH' | 'WITHDREW_BATCH_PARTIALLY' | 'WITHDREW_BATCH' | 'DISCARDED_SLOT' | 'RESTORED_SLOT';

  readonly batch_id: number;

  readonly user: User;
}

export class CPEActors {
  @Type(() => Company)
  titular_carta_de_porte?: Company;

  @Type(() => Company)
  remitente_comercial_productor?: Company = null;

  @Type(() => Company)
  remitente_comercial_venta_primaria?: Company = null;

  @Type(() => Company)
  remitente_comercial_venta_secundaria?: Company = null;

  @Type(() => Company)
  remitente_comercial_venta_secundaria_2?: Company = null;

  @Type(() => Company)
  mercado_a_termino?: Company = null;

  @Type(() => Company)
  corredor_venta_primaria?: Company = null;

  @Type(() => Company)
  corredor_venta_secundaria?: Company = null;

  @Type(() => Company)
  representante_entregador?: Company = null;

  @Type(() => Company)
  representante_recibidor?: Company = null;

  @Type(() => Company)
  destinatario?: Company = null;

  @Type(() => Company)
  destino?: Company = null;

  // @Type(() => Company)
  // empresa_transportista?: Company = null;

  // @Type(() => Company)
  // flete_pagador?: Company = null;

  // @Type(() => Company)
  // chofer?: Company = null;

  // @Type(() => Company)
  // intermediario_de_flete?: Company = null;

  @Type(() => Company)
  observaciones?: Company[] = null;

  constructor(data: Partial<CPEActors> = {}) {
    Object.assign(this, data);

    if (!data.observaciones) {
      this.observaciones = [];
    }
  }
}

export class ElectronicWaybill {
  @Type(() => CPEActors)
  actors?: CPEActors;

  /**
  * Inherits actor information (brokers, sellers, buyers) from a given entity
  * —either a {@link Contract} or a {@link Negotiation}— into the current instance's {@link CPEActors}.
  *
  * This method ensures that key actor roles in the CPE are properly populated based
  * on the data available in the source entity, while preserving any previously set actors.
  *
  * The logic applied is as follows:
  * - If brokers exist:
  *   - Assign the last broker as `corredor_venta_primaria` (primary sale broker) if not already set.
  *   - If another broker remains and differs from the primary, an observation is generated describing
  *     that broker’s intervention between seller and buyer.
  *   - All extra brokers are appended to `actors.observaciones`.
  * - If the seller exists and is not already the `destinatario`:
  *   - If the goods are the seller's own production (`own_production`, falling back to
  *     `companyCan(...).produce` when there is no data), assign it to `remitente_comercial_productor`.
  *   - Otherwise, assign it to `remitente_comercial_venta_primaria`.
  * - If the buyer exists and is not already the `destinatario`, assign it to
  *   `remitente_comercial_venta_secundaria` (if not already set).
  * - A commercial sender role is never set to the same company as `destinatario`, since a
  *   company cannot be both sender and recipient of the same Waybill.
  *
  * The method returns a formatted HTML string describing any broker intervention found.
  * If no broker relationship is identified, an empty string is returned.
  *
  * @param {Contract | Negotiation} entityObj - The source entity containing broker, seller, and buyer data.
  * @returns {string} A formatted HTML observation describing broker mediation, or an empty string if none apply.
  *
  * @property {CPEActors} this.actors - The local actor structure being populated.
  * @property {Company} [this.actors.corredor_venta_primaria] - The primary broker assigned if available.
  * @property {Company} [this.actors.remitente_comercial_productor] - The producer acting as the commercial sender.
  * @property {Company} [this.actors.remitente_comercial_venta_primaria] - The commercial sender for the primary sale.
  * @property {Company} [this.actors.remitente_comercial_venta_secundaria] - The commercial sender for the secondary sale.
  * @property {Company[]} [this.actors.observaciones] - A list of brokers added as observations.
  *
  * @example
  * const observations = this.inheritActors(contract);
  * console.log(observations);
  * // => "Interviene como corredor <b>Broker SA</b> entre <i>ACME SRL</i> y <i>Comercial del Sur</i>."
  */
  public inheritActors(entityObj: Contract | Negotiation): string {
    let observations = '';

    if (entityObj) {
      if (entityObj.brokers?.length) {
        let brokers = instanceToInstance(entityObj.brokers);
        // Set corredor_venta_primaria if there is none
        if (!this.actors.corredor_venta_primaria?.id) this.actors.corredor_venta_primaria = brokers.pop();

        if (brokers.length > 0 &&
          this.actors.corredor_venta_primaria.id != brokers[0].id) {
          // Add the rest to observations
          const seller = entityObj.seller?.name || 'Rte. Comercial Venta Primaria';
          const buyer = entityObj.buyer?.name || 'Rte. Comercial Venta Secundaria';
          observations = `Interviene como corredor <b>${brokers[0].name}</b> entre <i>${seller}</i> y <i>${buyer}</i>.`;
          this.actors.observaciones = [...(this.actors.observaciones || []), ...brokers];
        }
      }

      // El remitente comercial nunca puede coincidir con el destinatario de la CPE
      const isDestinatario = (actor?: Company): boolean =>
        !!actor?.id && actor.id === this.actors.destinatario?.id;

      if (entityObj.seller && !isDestinatario(entityObj.seller)) {
        // parseApiBoolean: la API puede serializar own_production como 1/0
        const isProducer = parseApiBoolean((entityObj as any).own_production)
          ?? companyCan(entityObj.seller).produce;

        if (isProducer) {
          // Set remitente_comercial_productor if there is none
          if (!this.actors.remitente_comercial_productor?.id) this.actors.remitente_comercial_productor = entityObj.seller;
        } else {
          // Set remitente_comercial_venta_primaria if there is none
          if (!this.actors.remitente_comercial_venta_primaria?.id) this.actors.remitente_comercial_venta_primaria = entityObj.seller;
        }
      }

      if (entityObj.buyer && !isDestinatario(entityObj.buyer) && !this.actors.remitente_comercial_venta_secundaria?.id) {
        // Set remitente_comercial_venta_secundaria if there is none
        this.actors.remitente_comercial_venta_secundaria = entityObj.buyer;
      }
    }

    return observations;
  }

  private _syngentaEntregador: Company;
  /**
   * Esta funcion es un hack para asignar a Williams como entregador y recibidor en la CPE
   * solo cuando la empresa que crea el lote es Syngenta y el destino no es Necochea.
   * 
   * @todo Mejorar este hack, idealmente desde el backend.
   */
  public syngentaEntregador(entityObj: Contract, company: Company, entregadorObservable: Observable<Company[]>): void {
    const normalize = (str: string): string => {
      return str.toUpperCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    };

    if ((!this.actors.representante_entregador || !this.actors.representante_recibidor) &&
      normalize(company.name).includes('SYNGENTA') &&
      entityObj.delivery?.locations?.length &&
      !normalize(entityObj.delivery.locations[0].location?.name).includes('NECOCHEA')) {
      const assign = () => {
        this.actors.representante_entregador = this.actors.representante_entregador || this._syngentaEntregador;
        this.actors.representante_recibidor = this.actors.representante_recibidor || this._syngentaEntregador;
      };

      if (this._syngentaEntregador) assign();
      else {
        entregadorObservable.subscribe(companies => {
          const firstCompany = companies[0];

          if (firstCompany?.id) {
            // Only if a Company is found, cache is updated
            this._syngentaEntregador = firstCompany;
            assign();
          }
        });
      }
    }
  }
}

/**
 * A Slot Batch is a container of {@link Slot|Slots}. {@link Slot|Slots} are assigned,
 * accepted or rejected in Batches.
 *
 * By reassigning Batches, new child Batches of the current Batch are created.
 * This creates a hierarchical Batch chain. In an ideal scenario, the first
 * Batch represents the receiving capacity of a terminal and the Batches at the
 * end of the chain should correspond to the {@link Company|Companies} that will
 * load the trucks.
 *
 *
 * ### Related UI components:
 * - [[SlotsComponent]]
 */
export class SlotsBatch {
  /** Agree's internal unique identifier. */
  id: number;
  /**
   * Reference to the first Batch in the tree.
   */
  reference: number;
  /**
   * Reference to the parent Batch.
   */
  parent_id: number;
  /**
   * Batch creation date.
   *
   * *Not to be confused with the date of receipt of the {@link Slot|Slots}.*
   */
  @Type(() => Date)
  date_issued: Date;

  status: {
    /**
     * ### Possible values
     * | id | description                           |
     * |---:|---------------------------------------|
     * |  1 | Sent to [[SlotsBatch.company]]        |
     * |  2 | Accepted by [[SlotsBatch.company]]    |
     * |  3 | Rejected by [[SlotsBatch.company]]    |
     * |  4 | Withdrawn by [[SlotsBatch.allocator]] |
     * |  5 | Returned by [[SlotsBatch.company]]    |
     */
    id: number;
    // type: string;
    /**
     * Use in case of rejection
     */
    reason?: string;
  }

  /**
   * {@link Company} that assigned the Batch.
   *
   * If there is a parent Batch it **MUST** match it's [[SlotsBatch.company|Company]].
   */
  @Type(() => Company)
  allocator?: Company;
  /**
   * Current {@link Slot|Slots} assignees.
   */
  assignees?: Assignee[];
  /**
   * {@link Company} that owns this Batch.
   */
  @Type(() => Company)
  company: Company;
  /**
   * Unassigned {@link Slot|Slots} in this Batch.
   */
  @Type(() => Slot)
  unassigned: Slot[];
  /**
   * Discarded {@link Slot|Slots} (only sent for root batches). They don't count anywhere.
   */
  @Type(() => Slot)
  discarded?: Slot[];
  /**
   * Children Batches with assigned {@link Slot|Slots} in this Batch.
   */
  @Type(() => SlotsBatch)
  assignments: SlotsBatch[];
  /**
   * If there is a related {@link Negotiation} to this Batch.
   *
   * Typed as 'any' to prevent Cannot access 'Negotiation' before initialization error.
   */
  @Type(() => Negotiation)
  negotiation?: any;
  /**
   * If there is a related {@link Contract} to this Batch.
   *
   * Typed as 'any' to prevent Cannot access 'Contract' before initialization error.
   */
  @Type(() => Contract)
  contract?: any;
  /**
   * Additional instructions or comments defined by the
   * [[SlotsBatch.allocator|Allocator]].
   */
  observations?: string;

  /**
   * All the {@link Company|Companies} involved in the Waybill.
   * @deprecated
   */
  actors?: {
    // holder: Company, // Will be defined in the last batch child
    intermediary: Company,
    commercial_sender: Company,
    buying_broker: Company,
    term_market: Company,
    selling_broker: Company,
    representative: Company,
    receiver: Company,
    destination: Company,
    carrier_broker: Company,
    carrier: Company,
    driver: Company
  };

  /**
   * Overwrite the values defined in [[SlotsBatch.actors]] in the {@link Waybill}.
   * @deprecated
   */
  waybillActors?: {
    intermediary?: WaybillActor,
    commercial_sender?: WaybillActor,
    buying_broker?: WaybillActor,
    term_market?: WaybillActor,
    selling_broker?: WaybillActor,
    representative?: WaybillActor,
    receiver?: WaybillActor,
    destination?: WaybillActor,
    carrier_broker?: WaybillActor,
    carrier?: WaybillActor,
    driver?: WaybillActor
  };

  /** Electronic Waybill. */
  @Type(() => ElectronicWaybill)
  cpe?: ElectronicWaybill;

  /**
   * Date this truck is expected at the terminal.
   */
  @Type(() => Date)
  date: Date;

  /**
   * {@link Company} at destination.
   */
  @Type(() => Company)
  recipient: Company;

  destination: Destination;

  @Type(() => Product)
  product?: Product;

  /** Extra quality conditions. */
  quality: any = {};

  readonly totalSlots?: number;

  /** ID of the SlotRequest that originated this batch. Null if manually assigned. */
  slot_request_id?: number;

  /** RENSPA del campo de origen (formato de ARCA `nn.nnn.n.nnnnn/nn`). Lo carga quien asigna. */
  renspa?: string;

  /**
   * Indicates whether the {@link SlotsBatch} has not being terminated or cancelled.
   */
  get isAlive(): boolean {
    return [1, 2].includes(this.status.id);
  }

  /**
   * Returns all the {@link Slot|Slots} in this Batch.
   */
  get slots(): Slot[] {
    if (!this._slots) this.mapSlots();
    return this._slots;
  }

  /** Discarded {@link Slot|Slots} of this root batch (estado "Descartados"). */
  get discarded_total(): number {
    return this.discarded?.length || 0;
  }

  /** Discarded {@link Slot|Slots} that were assigned to another company. */
  get discarded_assigned(): number {
    return (this.discarded || []).filter(slot => slot.discard?.assigned).length;
  }

  get discarded_unassigned(): number {
    return this.discarded_total - this.discarded_assigned;
  }

  /**
   * Returns the {@link Slot|Slots} quantity in this Batch.
   */
  get slots_total(): number {
    return this.slots.length || 0;
  }

  get slots_unassigned(): number {
    return (this.unassigned || []).length;
  }

  get slots_assigned(): number {
    return this.slots_total - this.slots_unassigned;
  }

  /** Slots with STOP status ACTIVADO (2), ARRIBADO (5), or DESCARGADO (3). */
  get slots_active(): number {
    return this.slots.filter(slot => slot.stop && [2, 3, 5].includes(slot.stop.status?.id)).length;
  }

  /** Slots with STOP status ACTIVADO (2). */
  get slots_activated(): number {
    return this.slots.filter(slot => slot.stop?.status?.id === 2).length;
  }

  /** Slots with STOP status ARRIBADO (5). */
  get slots_arrived(): number {
    return this.slots.filter(slot => slot.stop?.status?.id === 5).length;
  }

  /** Slots with STOP status DESCARGADO (3). */
  get slots_unloaded(): number {
    return this.slots.filter(slot => slot.stop?.status?.id === 3).length;
  }

  /** Slots unloaded (flag `unloaded` or STOP status DESCARGADO (3)) that have a CTG. */
  get slots_unloaded_ctg(): number {
    return this.slots.filter(slot => slot.ctg && (slot.unloaded || slot.stop?.status?.id === 3)).length;
  }

  /** Data from its slots. */
  private _slots: Slot[];

  private mapSlots(): void {
    this._slots = [];
    if (this.unassigned) this._slots = this._slots.concat(this.unassigned);
    if (this.assignments) this.assignments.forEach(batch => {
      if (![3, 4, 5].includes(batch.status.id)) this._slots = this._slots.concat(batch.slots);
    });
  }
}

export class Destination {
  /** Agree's internal unique identifier. */
  readonly id: number;
  /** Destination owner. */
  @Type(() => Company)
  company: Company;

  location: Location;
  address: Address;

  /** STOP id. */
  readonly idTerminal?: number;

  name: string;
  label: string;
  terminal?: string;

  constructor(data: Partial<Destination> = {}) {
    this.address = new Address();

    Object.assign(this, data);
  }
}

class WaybillActor {
  name: string;
  fiscal_id: string;
  editable: boolean;
}
